import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseRequest } from 'src/api/hooks/useApi.types';

import {
  useGetScheduledFacebookPosts,
  useScheduleFacebookPost,
  useUpdatePostSchedule,
  useUpdateFacebookPost,
} from 'src/api/hooks/useFacebookPost';
import { FacebookPostPayload } from 'src/api/hooks/useFacebookPost.types';
import { useGenerateFacebookPost } from 'src/api/hooks/useJob';
import { PostSchedule } from 'src/DTO/FacebookPost.type';
import { DEFAULTS } from 'src/pages/Facebook/components/constants';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';

export const useFacebookPosts = (
  getFacebookPosts?: BaseRequest<FacebookPostPayload>,
  jobId?: string,
) => {
  const { t } = useTranslation();

  const { NUMBER_OF_POSTS } = DEFAULTS;

  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);

  const [scheduledPosts, setScheduledPosts] = useState<PostSchedule[]>([]);

  const [generatedText, setGeneratedText] = useState<string>('');
  const [generatedImage, setGeneratedImage] = useState<string>('');

  const [textGenerationLoading, setTextLoading] = useState<boolean[]>(
    new Array(NUMBER_OF_POSTS).fill(false),
  );
  const [imageGenerationLoading, setImageLoading] = useState<boolean[]>(
    new Array(NUMBER_OF_POSTS).fill(false),
  );

  useEffect(() => {
    setTextLoading(new Array(NUMBER_OF_POSTS).fill(false));
    setImageLoading(new Array(NUMBER_OF_POSTS).fill(false));
  }, [generatedText, generatedImage]);

  const handleSuccess = (message: string) => {
    setPopUpData({
      isOpen: true,
      type: PopUpEnum.success,
      message: t(message),
    });
  };

  const handleFailure = (message: string) => {
    setPopUpData({
      isOpen: true,
      type: PopUpEnum.error,
      message: t(message),
    });
  };

  const [generateFbPost, { loading: generateFbPostLoading }] = useGenerateFacebookPost({
    onComplete: res => {
      if (res) {
        if (res.texts) {
          setGeneratedText(res.texts);
        }
        if (res.images) {
          setGeneratedImage(res.images);
        }
        if (getFacebookPosts && jobId) {
          getFacebookPosts({ variables: { jobId } });
        }

        handleSuccess('facebookPost.scheduling.alerts.generatePost.success');
      }
    },
    onError: () => {
      handleFailure('facebookPost.scheduling.alerts.generatePost.error');
    },
  });

  const [getScheduledPosts, { loading: getScheduledPostsLoading }] = useGetScheduledFacebookPosts({
    onComplete: res => {
      if (res?.jobsWithSchedules) {
        const mappedSchedules = res.jobsWithSchedules.map(job => {
          const schedule = job.postSchedule;

          const { jobId, name, city, ...scheduleDetails } = schedule;

          return {
            jobId: job?.id,
            name: job?.role?.name || job?.role?.localizedData?.en?.name,
            city: job?.role?.city || '',
            ...scheduleDetails,
          };
        });

        setScheduledPosts(mappedSchedules);
      }
    },
    onError: () => {
      handleFailure('facebookPost.scheduling.alerts.getScheduledPosts.error');
    },
  });

  const [scheduleFacebookPost] = useScheduleFacebookPost({
    onComplete: () => {
      handleSuccess('facebookPost.scheduling.alerts.schedulePost.success');
    },
    onError: () => {
      handleFailure('facebookPost.scheduling.alerts.schedulePost.error');
    },
  });

  const [updateScheduledPost] = useUpdatePostSchedule({
    onComplete: () => {
      handleSuccess('facebookPost.scheduling.alerts.updateScheduledPost.success');
    },
    onError: () => {
      handleFailure('facebookPost.scheduling.alerts.updateScheduledPost.error');
    },
  });

  const [updateFacebookPost] = useUpdateFacebookPost({
    onComplete: () => {
      handleSuccess('facebookPost.scheduling.alerts.updatePost.success');
    },
    onError: () => {
      handleFailure('facebookPost.scheduling.alerts.updatePost.error');
    },
  });

  return {
    generatedText,
    generatedImage,
    scheduledPosts,
    textGenerationLoading,
    imageGenerationLoading,
    getScheduledPostsLoading,
    generateFbPost,
    setTextLoading,
    setImageLoading,
    scheduleFacebookPost,
    updateFacebookPost,
    getScheduledPosts,
    setScheduledPosts,
    updateScheduledPost,
    setGeneratedText,
    setGeneratedImage,
    generateFbPostLoading,
  };
};
